import { motion, AnimatePresence } from "framer-motion";
import { PhoneOff } from "lucide-react";
import { HelcooLogo } from "../HelcooLogo";

/**
 * VoiceCallScreen - شاشة المكالمة الصوتية الاحترافية
 * تصميم نظيف بدون نصوص - فقط اللوغو والدوائر المتناغمة
 */
export function VoiceCallScreen({ 
  isOpen, 
  onClose, 
  isRecording, 
  isSpeaking, 
  isProcessing,
  t 
}) {
  // تحديد ما إذا كان يجب إظهار الدوائر المتحركة
  const showWaves = isRecording || isSpeaking;
  
  // لون التوهج حسب الحالة
  const glowColor = isRecording 
    ? "rgba(239, 68, 68, 0.4)" // أحمر للتسجيل
    : isSpeaking 
    ? "rgba(34, 197, 94, 0.4)" // أخضر عندما AI يتكلم
    : "rgba(255, 255, 255, 0.1)"; // شفاف للانتظار

  // نص الحالة
  const statusText = isRecording 
    ? "🎙️ تحدث الآن..."
    : isProcessing 
    ? "⏳ جاري المعالجة..."
    : isSpeaking 
    ? "🔊 HELCOO يتحدث..."
    : "🎤 اضغط للتحدث";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center"
          style={{ 
            paddingTop: 'env(safe-area-inset-top)', 
            paddingBottom: 'env(safe-area-inset-bottom)' 
          }}
          data-testid="voice-call-screen"
        >
          {/* خلفية متدرجة */}
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-black to-neutral-950" />
          
          {/* نص الحالة في الأعلى - يظهر دائماً */}
          <motion.div 
            className="absolute top-16 text-center z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.p 
              className="text-white text-xl font-semibold"
              key={statusText}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              {statusText}
            </motion.p>
          </motion.div>

          {/* اللوغو مع الدوائر المتناغمة */}
          <div className="relative flex items-center justify-center">
            
            {/* الدوائر المتحركة - تظهر فقط عند التسجيل أو التحدث */}
            <AnimatePresence>
              {showWaves && (
                <>
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="absolute rounded-full"
                      style={{ 
                        border: `2px solid ${isRecording ? 'rgba(239, 68, 68, 0.25)' : 'rgba(34, 197, 94, 0.25)'}`,
                        width: 160,
                        height: 160
                      }}
                      initial={{ scale: 1, opacity: 0 }}
                      animate={{ 
                        scale: [1, 2 + i * 0.3], 
                        opacity: [0.4, 0] 
                      }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: i * 0.8,
                        ease: "linear"
                      }}
                    />
                  ))}
                </>
              )}
            </AnimatePresence>
            
            {/* التوهج الداخلي */}
            <motion.div
              className="absolute rounded-full"
              style={{
                width: 200,
                height: 200,
                background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`
              }}
              animate={{
                scale: showWaves ? [1, 1.15, 1] : 1,
                opacity: showWaves ? [0.8, 1, 0.8] : 0.3
              }}
              transition={{
                duration: 2,
                repeat: showWaves ? Infinity : 0,
                ease: "easeInOut"
              }}
            />

            {/* اللوغو الرئيسي - استخدام اللوغو الحقيقي */}
            <motion.div
              className="relative z-10"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              style={{
                filter: showWaves 
                  ? `drop-shadow(0 0 30px ${isRecording ? 'rgba(239,68,68,0.5)' : 'rgba(34,197,94,0.5)'})`
                  : 'drop-shadow(0 0 20px rgba(255,255,255,0.2))'
              }}
            >
              <HelcooLogo size={140} animated={false} showBadge={true} />
            </motion.div>
          </div>

          {/* مؤشر الصوت البسيط عند التسجيل */}
          <AnimatePresence>
            {isRecording && (
              <motion.div 
                className="absolute bottom-40 flex items-center gap-1.5"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={`wave-bar-${i}`}
                    className="w-1.5 rounded-full bg-red-500"
                    animate={{
                      height: [12, 32, 12],
                    }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* زر إنهاء المكالمة فقط */}
          <motion.div 
            className="absolute bottom-20 z-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="w-16 h-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-2xl"
              style={{ boxShadow: "0 0 40px rgba(239,68,68,0.5)" }}
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-7 h-7" />
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default VoiceCallScreen;
